// Client entry point. Importing each module registers its custom element.
import './components/ui/button';
import './components/ui/input';
import './components/ui/loading';
import './components/ui/toast';
import './components/ui/modal';
import './components/ui/badge';
import './components/ui/avatar';
import './components/ui/card';
import './components/ui/stat-card';
import './components/ui/empty-state';
import './components/ui/select';
import './components/ui/currency-select';
import './components/ui/checkbox';
import './components/ui/toggle';
import './components/ui/tabs';
import './components/ui/table';
import './components/ui/photo-upload';
import './components/ui/markdown-renderer';
import './components/ui/rich-text-editor';
import './components/ui/file-upload';
import './components/ui/upload-queue';
import './components/ui/tag-picker';
import './components/shared/sidebar';
import './components/shared/header';
import './components/shared/notification-bell';
import './components/shared/portal-nav';
import './components/shared/break-banner';
import './components/shared/cookie-notice';
import './components/shared/user-menu';
import './components/shared/lang-menu';
import './pages/auth/login-page';
import './pages/auth/forgot-password-page';
import './pages/auth/reset-password-page';
import './pages/admin/dashboard-page';
import './pages/admin/teacher-dashboard';
import './pages/admin/placeholder-page';
import './pages/admin/students-page';
import './pages/admin/student-detail-page';
import './pages/admin/teachers-page';
import './pages/admin/teacher-detail-page';
import './pages/admin/families-page';
import './pages/admin/accounting-page';
import './pages/admin/reporting-page';
import './pages/admin/classes-page';
import './pages/admin/class-detail-page';
import './pages/admin/event-form-modal';
import './pages/admin/events-page';
import './pages/admin/event-detail-page';
import './pages/admin/event-todos';
import './pages/admin/event-notes';
import './pages/admin/event-files';
import './pages/admin/event-details-tab';
import './pages/admin/media-page';
import './pages/admin/mailing-page';
import './pages/admin/mailing-lists';
import './pages/admin/mailing-emails';
import './pages/admin/audit-page';
import './pages/admin/settings-page';
import './pages/onboarding/onboarding-page';
import './pages/setup/setup-wizard-page';
import './pages/portal/portal-login-page';
import './pages/portal/portal-dashboard';
import './pages/portal/portal-classes';
import './pages/portal/portal-levels';
import './pages/portal/portal-payments';
import './pages/portal/portal-calendar';
import './pages/portal/portal-events';
import './pages/portal/portal-event-detail';
import './pages/portal/portal-profile';
import './pages/portal/portal-contract';
import './pages/portal/portal-gallery';
// Discipline modules — one import per module registers its components + descriptor.
import './modules/irish-dance';
import './components/cadence-app';

import { store, type SessionUser } from './store';
import { router } from './router';
import { api } from './api';
import { setLocale, getLocale, LOCALE_STORAGE_KEY } from './i18n';

/** Locale the visitor previously chose (persisted by the language switcher). */
function savedLocale(): string | null {
  try { return localStorage.getItem(LOCALE_STORAGE_KEY); } catch { return null; }
}

// Apply persisted theme as early as possible (runs before the app renders).
// Dark/light: a saved choice wins; otherwise honour the OS preference on first visit.
// Theme palette: apply the cached school theme id so there's no first-paint flash.
try {
  const savedDark = localStorage.getItem('cadence.theme');
  if (savedDark) document.documentElement.dataset.theme = savedDark;
  else if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) document.documentElement.dataset.theme = 'dark';
  const savedThemeId = localStorage.getItem('cadence.theme-id');
  if (savedThemeId) document.documentElement.dataset.themeId = savedThemeId;
} catch {
  /* storage unavailable — fall back to default light Ocean theme */
}

async function boot(): Promise<void> {
  // Apply any previously chosen locale up front so it holds regardless of how the
  // session/brand fetches resolve (an authed user's saved preference still wins below).
  const saved = savedLocale();
  if (saved) setLocale(saved);
  // First-run detection: a freshly provisioned (migrated-but-unseeded) deployment has no
  // admin/school yet, so the app routes to the setup wizard until it's initialized.
  try {
    const setup = await api.get<{ initialized: boolean }>('/setup/status');
    store.set({ setupComplete: setup.initialized });
  } catch {
    // Network/endpoint failure — leave setupComplete true so users aren't trapped.
  }
  // Restore an existing session (HttpOnly cookie → /auth/session). This endpoint
  // returns { user: null } (200) when unauthenticated, so no console error on load.
  try {
    const { user, features, breakPeriod, school } = await api.get<{ user: SessionUser | null; features?: Record<string, boolean>; breakPeriod?: boolean; school?: { name: string; logo_url: string | null; default_language?: string; theme_id?: string } }>('/auth/session');
    if (user) {
      store.set({ user, features: features ?? {}, breakPeriod: breakPeriod === true, school: school ?? null });
      // A user's own preference wins; otherwise fall back to the school default.
      setLocale(user.language_preference || school?.default_language || 'en');
      store.applyTheme(user.dark_mode);
      store.applyThemeId(school?.theme_id || 'ocean');
    } else {
      // Pre-login: pull the school brand so the login screen + tab title use the
      // school name, its default language, and its theme.
      try {
        const brand = await api.get<{ name: string; logo_url: string | null; default_language?: string; theme_id?: string }>('/auth/brand');
        store.set({ school: { name: brand.name, logo_url: brand.logo_url, default_language: brand.default_language, theme_id: brand.theme_id } });
        store.applyThemeId(brand.theme_id || 'ocean');
        // A visitor's explicit choice wins over the school default.
        setLocale(savedLocale() || brand.default_language || 'en');
      } catch {
        if (savedLocale()) setLocale(savedLocale()!);
      }
    }
  } catch {
    // Network error — proceed unauthenticated; the router guard routes to /login.
  } finally {
    store.set({ locale: getLocale(), ready: true });
    router.start();
  }
}

boot();
